'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  roles: string[];
  tenant_id: string;
}

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  expiresAt: number | null;

  isAuthenticated: () => boolean;
  /**
   * Login. Visszatérési érték:
   *  - `{ status: 'ok' }` — sikeres belépés, access_token state-be írva.
   *  - `{ status: 'mfa_required', mfaChallengeToken, expiresIn }` — MFA második
   *    lépés szükséges; a hívó a `/mfa/verify` page-re navigál a challenge-token-nel.
   */
  login: (email: string, password: string) => Promise<LoginResult>;
  /** MFA második lépés: a `/auth/mfa/verify`-re POST, access_token state-be.
   *  `rememberDevice=true` esetén a backend httpOnly eszköz-cookie-t állít be,
   *  így a következő belépéseken ezen a böngészőn 10 napig nincs MFA (csak
   *  nem-admin szerepkörre — a szerver kényszeríti). */
  verifyMfa: (challengeToken: string, totpCode: string, rememberDevice?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
  fetchMe: () => Promise<void>;
}

/** Integrátor-fókuszú user: van `integration` szerepe, de se admin, se
 *  librarian. Neki az admin felületen csak az Integrációk oldal releváns —
 *  a tartalmi/műveleti végpontok (dashboard, publikációk, queue, statisztika)
 *  403-at adnának. A sidebar és a `/` kezdőlap erre szűkíti a nézetét. */
export function isIntegrationOnly(user: AuthUser | null): boolean {
  if (!user) return false;
  return (
    user.roles.includes('integration') &&
    !user.roles.includes('admin') &&
    !user.roles.includes('librarian')
  );
}

export type LoginResult =
  | { status: 'ok'; mfaEnrollmentRequired: boolean }
  | { status: 'mfa_required'; mfaChallengeToken: string; expiresIn: number; trustedDeviceAllowed: boolean };

const TENANT = 'default';

// A refresh-token httpOnly + Secure + SameSite=Lax cookie-ban (`lectura_rt`),
// amelyet a backend a login válaszban állít be. Path=/ — a Next.js proxy-flow
// (`/api/identity/auth/refresh`) miatt SZÁNDÉKOSAN nem szűkítjük (a backend
// `_REFRESH_COOKIE_PATH = "/"`; szűkebb Path törné a proxy-flow-t, mert a
// böngésző nem küldené a cookie-t a `/api/identity/...` útvonalra). A CSRF-et a
// SameSite=Lax + httpOnly korlátozza. A frontend JS NEM látja és NEM tárolja,
// így XSS esetén sem lopható el. A `credentials: 'include'` minden auth-fetch-en
// bekapcsolva, hogy a cookie automatikusan menjen.
const AUTH_FETCH_OPTS: Partial<RequestInit> = { credentials: 'include' };

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      user: null,
      expiresAt: null,

      isAuthenticated: () => {
        const s = get();
        if (!s.accessToken || !s.expiresAt) return false;
        // 30 mp puffer
        return s.expiresAt > Date.now() + 30_000;
      },

      login: async (email, password) => {
        const res = await fetch('/api/identity/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-Id': TENANT,
          },
          body: JSON.stringify({ email, password }),
          ...AUTH_FETCH_OPTS,
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Belépés sikertelen (${res.status}): ${text.slice(0, 200)}`);
        }
        const data = await res.json();
        // Az `/auth/login` válasza vagy TokenPair, vagy MfaChallenge
        // (lásd identity-svc/app/schemas.py:LoginResponse union).
        if (data.requires_mfa) {
          return {
            status: 'mfa_required',
            mfaChallengeToken: data.mfa_challenge_token,
            expiresIn: data.expires_in,
            trustedDeviceAllowed: Boolean(data.trusted_device_allowed),
          };
        }
        set({
          accessToken: data.access_token,
          expiresAt: Date.now() + data.expires_in * 1000,
        });
        await get().fetchMe();
        return {
          status: 'ok',
          mfaEnrollmentRequired: Boolean(data.mfa_enrollment_required),
        };
      },

      verifyMfa: async (challengeToken, totpCode, rememberDevice = false) => {
        const res = await fetch('/api/identity/auth/mfa/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mfa_challenge_token: challengeToken,
            totp_code: totpCode,
            remember_device: rememberDevice,
          }),
          ...AUTH_FETCH_OPTS,
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`MFA ellenőrzés sikertelen (${res.status}): ${text.slice(0, 200)}`);
        }
        const data = await res.json();
        set({
          accessToken: data.access_token,
          expiresAt: Date.now() + data.expires_in * 1000,
        });
        await get().fetchMe();
      },

      logout: async () => {
        await fetch('/api/identity/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          ...AUTH_FETCH_OPTS,
        }).catch(() => {});
        set({ accessToken: null, user: null, expiresAt: null });
      },

      refresh: async () => {
        // A refresh cookie automatikusan elmegy a credentials: 'include' miatt.
        const res = await fetch('/api/identity/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          ...AUTH_FETCH_OPTS,
        });
        if (!res.ok) {
          set({ accessToken: null, user: null, expiresAt: null });
          return false;
        }
        const data = await res.json();
        set({
          accessToken: data.access_token,
          expiresAt: Date.now() + data.expires_in * 1000,
        });
        return true;
      },

      fetchMe: async () => {
        const { accessToken } = get();
        if (!accessToken) return;
        const res = await fetch('/api/identity/users/me', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.ok) {
          const u = await res.json();
          set({ user: { id: u.id, email: u.email, display_name: u.display_name, roles: u.roles, tenant_id: u.tenant_id } });
        }
      },
    }),
    {
      name: 'lectura-admin-auth',
      // Verzió-bump: a régi persist-szelvényekben még benne van a `refreshToken`.
      // A bump miatt a Zustand újrahidratál — XSS-vektor a régi localStorage-mezőből
      // nem maradhat fenn.
      version: 2,
      partialize: (s) => ({
        accessToken: s.accessToken,
        user: s.user,
        expiresAt: s.expiresAt,
      }),
      migrate: (persisted, version) => {
        // v1 → v2: a refreshToken mezőt dobjuk.
        // A `as unknown` köztes cast szükséges, mert a Record<string, unknown>
        // és az AuthState között a TS nem lát közvetlen átfedést.
        if (version < 2 && persisted && typeof persisted === 'object') {
          const { refreshToken: _drop, ...rest } = persisted as Record<string, unknown>;
          return rest as unknown as AuthState;
        }
        return persisted as AuthState;
      },
    },
  ),
);

/** Authentikált fetch — minden request-be beleteszi a Bearer fejlécet,
 *  és ha 401-et kap, megpróbál refresh-elni egyszer.
 */
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const auth = useAuth.getState();
  const headers = new Headers(init.headers ?? {});

  if (!headers.has('X-Tenant-Id')) headers.set('X-Tenant-Id', TENANT);

  let token = auth.accessToken;
  if (token && !auth.isAuthenticated()) {
    const ok = await auth.refresh();
    token = ok ? useAuth.getState().accessToken : null;
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let res = await fetch(input, { ...init, headers });

  if (res.status === 401) {
    // A refresh-cookie még megpróbál érvényes lenni — egy refresh-elés a backend
    // dolga (a cookie automatikusan elmegy). Ha nem sikerül, kijelentkezünk.
    const ok = await auth.refresh();
    if (ok) {
      headers.set('Authorization', `Bearer ${useAuth.getState().accessToken}`);
      res = await fetch(input, { ...init, headers });
    }
  }
  return res;
}

/**
 * Authentikált fetch JSON body-val.
 * A frontend camelCase-ben dolgozik, a Pydantic backend snake_case-t vár,
 * ezért a body-t mindig konvertáljuk küldés előtt. Ezzel a hívók nem
 * kell, hogy minden mutáció-helyen kézzel végezzék el a konverziót.
 */
export async function authFetchJson(
  url: string,
  options: { method: 'POST' | 'PATCH' | 'PUT' | 'DELETE'; body?: unknown } = { method: 'POST' },
): Promise<Response> {
  // Lazy import a circular-dep elkerülésére (api.ts is importálja az authFetch-t)
  const { camelToSnakeKeys } = await import('./api');
  const init: RequestInit = {
    method: options.method,
    headers: options.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(camelToSnakeKeys(options.body));
  }
  return authFetch(url, init);
}
